import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useRef } from "react";
import { useSelector } from "react-redux";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  HandledGraphQLMutationError
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  listPaymentsQuery,
  getPaymentQuery,
  createMembershipPaymentMutation,
  createIremboInvoiceMutation,
  reconcileIremboPaymentMutation,
  initiateIremboMobileMoneyMutation
} from "../../../Graphql/querries/paymentQuery";
import {
  ListPaymentsResponse,
  GetPaymentResponse,
  ListPaymentsInput,
  GetPaymentInput,
  CreateMembershipPaymentResponse,
  CreateMembershipPaymentInput,
  CreateIremboInvoiceGraphqlResponse,
  CreateIremboInvoiceInput,
  ReconcileIremboPaymentResponse,
  ReconcileIremboPaymentInput,
  InitiateIremboMobileMoneyResponse,
  InitiateIremboMobileMoneyInput
} from "../../../Graphql/types/payment";
import { RootState } from "../../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../../Utils/appLogger";
import {
  trackClarityEvent,
  upgradeClaritySession
} from "../../../analytics/clarity";

export const useGetPaymentList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ListPaymentsResponse, Error, ListPaymentsInput>({
    mutationKey: ["listPayments"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching payment list",
        buildLogContext(
          "membership",
          "list-payments",
          userId,
          companyId,
          storeId
        )
      );

      const response: ListPaymentsResponse = await fetchGraphQL(
        listPaymentsQuery,
        { input },
        setAlert
      );

      const listResult = response?.listPayments;

      // ❌ Unexpected response shape
      if (!listResult) {
        captureAppException(
          "List payments failed: missing listPayments",
          new Error("List payments failed: missing listPayments"),
          buildLogContext(
            "membership",
            "list-payments",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "List payments failed: missing listPayments"
        );
      }

      // ⚠️ Expected API/business failure
      if (listResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "List payments rejected by API",
          buildLogContext(
            "membership",
            "list-payments",
            userId,
            companyId,
            storeId,
            { apiCode: String(listResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: listResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `List payments rejected by API: ${listResult.code}`,
          listResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Payment list fetched successfully",
        buildLogContext(
          "membership",
          "list-payments",
          userId,
          companyId,
          storeId,
          {
            paymentCount: Array.isArray(listResult.paymentList)
              ? listResult.paymentList.length
              : 0
          }
        )
      );

      return response;
    },

    onError: (error) => {
      // fetchGraphQL already called setAlert and captureAppException via the
      // shared client for network/runtime errors — don't repeat either here.
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to fetch payment list",
        error,
        buildLogContext(
          "membership",
          "list-payments",
          userId,
          companyId,
          storeId
        )
      );
    }
  });
};

export const useGetPaymentDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<GetPaymentResponse, Error, GetPaymentInput>({
    mutationKey: ["getPayment"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching payment detail",
        buildLogContext(
          "membership",
          "get-payment",
          userId,
          companyId,
          storeId,
          { paymentId: String(input.paymentId) }
        )
      );

      const response: GetPaymentResponse = await fetchGraphQL(
        getPaymentQuery,
        { input },
        setAlert
      );

      const getResult = response?.getPayment;

      // ❌ Unexpected response shape
      if (!getResult) {
        captureAppException(
          "Get payment failed: missing getPayment",
          new Error("Get payment failed: missing getPayment"),
          buildLogContext(
            "membership",
            "get-payment",
            userId,
            companyId,
            storeId,
            { paymentId: String(input.paymentId) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Get payment failed: missing getPayment"
        );
      }

      // ⚠️ Expected API/business failure
      if (getResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Get payment rejected by API",
          buildLogContext(
            "membership",
            "get-payment",
            userId,
            companyId,
            storeId,
            {
              paymentId: String(input.paymentId),
              apiCode: String(getResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: getResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Get payment rejected by API: ${getResult.code}`,
          getResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Payment detail fetched successfully",
        buildLogContext(
          "membership",
          "get-payment",
          userId,
          companyId,
          storeId,
          { paymentId: String(input.paymentId) }
        )
      );

      return response;
    },

    onError: (error, input) => {
      // fetchGraphQL already called setAlert and captureAppException via the
      // shared client for network/runtime errors — don't repeat either here.
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to fetch payment detail",
        error,
        buildLogContext(
          "membership",
          "get-payment",
          userId,
          companyId,
          storeId,
          { paymentId: String(input?.paymentId) }
        )
      );
    }
  });
};

export const useCreateMembershipPayment = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    CreateMembershipPaymentResponse,
    Error,
    CreateMembershipPaymentInput
  >({
    mutationKey: ["createMembershipPayment"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating membership payment",
        buildLogContext(
          "membership",
          "create-membership-payment",
          userId,
          companyId,
          storeId
        )
      );

      const response: CreateMembershipPaymentResponse = await fetchGraphQL(
        createMembershipPaymentMutation,
        { input },
        setAlert
      );

      const createResult = response?.createMembershipPayment;

      // ❌ Unexpected response shape
      if (!createResult) {
        captureAppException(
          "Create membership payment failed: missing createMembershipPayment",
          new Error(
            "Create membership payment failed: missing createMembershipPayment"
          ),
          buildLogContext(
            "membership",
            "create-membership-payment",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Create membership payment failed: missing createMembershipPayment"
        );
      }

      // ⚠️ Expected API/business failure
      if (createResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Membership payment creation rejected by API",
          buildLogContext(
            "membership",
            "create-membership-payment",
            userId,
            companyId,
            storeId,
            { apiCode: String(createResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: createResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Membership payment creation rejected by API: ${createResult.code}`,
          createResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Membership payment created successfully",
        buildLogContext(
          "membership",
          "create-membership-payment",
          userId,
          companyId,
          storeId,
          { paymentId: String(createResult.payment?.paymentId ?? "") }
        )
      );
      trackClarityEvent("membership_checkout_started");

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to create membership payment",
        error,
        buildLogContext(
          "membership",
          "create-membership-payment",
          userId,
          companyId,
          storeId
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export const useCreateIremboInvoice = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    CreateIremboInvoiceGraphqlResponse,
    Error,
    CreateIremboInvoiceInput
  >({
    mutationKey: ["createIremboInvoice"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating Irembo invoice",
        buildLogContext(
          "membership",
          "create-irembo-invoice",
          userId,
          companyId,
          storeId
        )
      );

      const response: CreateIremboInvoiceGraphqlResponse = await fetchGraphQL(
        createIremboInvoiceMutation,
        { input },
        setAlert
      );

      const createResult = response?.createIremboInvoice;

      // ❌ Unexpected response shape
      if (!createResult) {
        captureAppException(
          "Create Irembo invoice failed: missing createIremboInvoice",
          new Error(
            "Create Irembo invoice failed: missing createIremboInvoice"
          ),
          buildLogContext(
            "membership",
            "create-irembo-invoice",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Create Irembo invoice failed: missing createIremboInvoice"
        );
      }

      // ⚠️ Expected API/business failure
      if (createResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Irembo invoice creation rejected by API",
          buildLogContext(
            "membership",
            "create-irembo-invoice",
            userId,
            companyId,
            storeId,
            { apiCode: String(createResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: createResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Irembo invoice creation rejected by API: ${createResult.code}`,
          createResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Irembo invoice created successfully",
        buildLogContext(
          "membership",
          "create-irembo-invoice",
          userId,
          companyId,
          storeId,
          { transactionId: String(createResult.invoice?.transactionId ?? "") }
        )
      );

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to create Irembo invoice",
        error,
        buildLogContext(
          "membership",
          "create-irembo-invoice",
          userId,
          companyId,
          storeId
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export const useReconcileIremboPayment = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    ReconcileIremboPaymentResponse,
    Error,
    ReconcileIremboPaymentInput
  >({
    mutationKey: ["reconcileIremboPayment"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started reconciling Irembo payment",
        buildLogContext(
          "membership",
          "reconcile-irembo-payment",
          userId,
          companyId,
          storeId,
          { invoiceId: String(input.invoiceId ?? "") }
        )
      );

      const response: ReconcileIremboPaymentResponse = await fetchGraphQL(
        reconcileIremboPaymentMutation,
        { input },
        setAlert
      );

      const reconcileResult = response?.reconcileIremboPayment;

      // ❌ Unexpected response shape
      if (!reconcileResult) {
        captureAppException(
          "Reconcile Irembo payment failed: missing reconcileIremboPayment",
          new Error(
            "Reconcile Irembo payment failed: missing reconcileIremboPayment"
          ),
          buildLogContext(
            "membership",
            "reconcile-irembo-payment",
            userId,
            companyId,
            storeId,
            { invoiceId: String(input.invoiceId ?? "") }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Reconcile Irembo payment failed: missing reconcileIremboPayment"
        );
      }

      // ⚠️ Expected API/business failure
      if (reconcileResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Irembo payment reconciliation rejected by API",
          buildLogContext(
            "membership",
            "reconcile-irembo-payment",
            userId,
            companyId,
            storeId,
            {
              invoiceId: String(input.invoiceId ?? ""),
              apiCode: String(reconcileResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: reconcileResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Irembo payment reconciliation rejected by API: ${reconcileResult.code}`,
          reconcileResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Irembo payment reconciled successfully",
        buildLogContext(
          "membership",
          "reconcile-irembo-payment",
          userId,
          companyId,
          storeId,
          { invoiceId: String(input.invoiceId ?? "") }
        )
      );
      trackClarityEvent("membership_payment_success");
      upgradeClaritySession("payment");

      return response;
    },

    onError: (error, input) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to reconcile Irembo payment",
        error,
        buildLogContext(
          "membership",
          "reconcile-irembo-payment",
          userId,
          companyId,
          storeId,
          { invoiceId: String(input?.invoiceId ?? "") }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export const useInitiateIremboMobileMoney = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    InitiateIremboMobileMoneyResponse,
    Error,
    InitiateIremboMobileMoneyInput
  >({
    mutationKey: ["initiateIremboMobileMoney"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started initiating Irembo mobile money",
        buildLogContext(
          "membership",
          "initiate-irembo-mobile-money",
          userId,
          companyId,
          storeId,
          { paymentId: String(input.paymentId ?? "") }
        )
      );

      const response: InitiateIremboMobileMoneyResponse = await fetchGraphQL(
        initiateIremboMobileMoneyMutation,
        { input },
        setAlert
      );

      const initiateResult = response?.initiateIremboMobileMoney;

      // ❌ Unexpected response shape
      if (!initiateResult) {
        captureAppException(
          "Initiate Irembo mobile money failed: missing initiateIremboMobileMoney",
          new Error(
            "Initiate Irembo mobile money failed: missing initiateIremboMobileMoney"
          ),
          buildLogContext(
            "membership",
            "initiate-irembo-mobile-money",
            userId,
            companyId,
            storeId,
            { paymentId: String(input.paymentId ?? "") }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Initiate Irembo mobile money failed: missing initiateIremboMobileMoney"
        );
      }

      // ⚠️ Expected API/business failure
      if (initiateResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Irembo mobile money initiation rejected by API",
          buildLogContext(
            "membership",
            "initiate-irembo-mobile-money",
            userId,
            companyId,
            storeId,
            {
              paymentId: String(input.paymentId ?? ""),
              apiCode: String(initiateResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: initiateResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Irembo mobile money initiation rejected by API: ${initiateResult.code}`,
          initiateResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Irembo mobile money initiated successfully",
        buildLogContext(
          "membership",
          "initiate-irembo-mobile-money",
          userId,
          companyId,
          storeId,
          { paymentId: String(input.paymentId ?? "") }
        )
      );

      return response;
    },

    onError: (error, input) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to initiate Irembo mobile money",
        error,
        buildLogContext(
          "membership",
          "initiate-irembo-mobile-money",
          userId,
          companyId,
          storeId,
          { paymentId: String(input?.paymentId ?? "") }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * Poll payment status after Irembo checkout until COMPLETED / FAILED or disabled.
 * useQuery — no start log since this fires repeatedly on an interval;
 * only log terminal states once per payment ID and unexpected errors.
 */
export const usePollMembershipPayment = (
  paymentId: string | undefined,
  enabled: boolean
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  // Track which paymentIds have already had their terminal state logged
  // so window-focus refetches don't emit duplicate log events
  const loggedTerminalRef = useRef<Set<string>>(new Set());

  return useQuery({
    queryKey: ["membershipPaymentStatus", paymentId],
    enabled: Boolean(enabled && paymentId),
    retry: false,

    queryFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      const response = await fetchGraphQL<
        GetPaymentResponse,
        { input: { paymentId: string } }
      >(getPaymentQuery, { input: { paymentId: paymentId! } }, setAlert);

      const getResult = response?.getPayment;

      // ❌ Unexpected response shape
      if (!getResult) {
        captureAppException(
          "Poll membership payment failed: missing getPayment",
          new Error("Poll membership payment failed: missing getPayment"),
          buildLogContext(
            "membership",
            "poll-membership-payment",
            userId,
            companyId,
            storeId,
            { paymentId: String(paymentId) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Poll membership payment failed: missing getPayment"
        );
      }

      // ⚠️ Expected API/business failure — stop polling, alert, throw
      if (getResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Poll membership payment rejected by API",
          buildLogContext(
            "membership",
            "poll-membership-payment",
            userId,
            companyId,
            storeId,
            {
              paymentId: String(paymentId),
              apiCode: String(getResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: getResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Poll membership payment rejected by API: ${getResult.code}`,
          getResult.code
        );
      }

      const status = getResult.payment?.paymentStatus;

      // ✅ Log terminal states only once per paymentId to avoid duplicate
      // log events on window-focus refetches
      if (
        (status === "COMPLETED" || status === "FAILED") &&
        paymentId &&
        !loggedTerminalRef.current.has(paymentId)
      ) {
        loggedTerminalRef.current.add(paymentId);
        sendAppLog(
          status === "COMPLETED" ? LOG_LEVEL.INFO : LOG_LEVEL.WARNING,
          `Membership payment polling ended with status: ${status}`,
          buildLogContext(
            "membership",
            "poll-membership-payment",
            userId,
            companyId,
            storeId,
            { paymentId: String(paymentId), paymentStatus: status }
          )
        );
        if (status === "COMPLETED") {
          trackClarityEvent("membership_payment_success");
          upgradeClaritySession("payment");
        }
      }

      return response;
    },

    refetchInterval: (query) => {
      // Stop immediately if the query has errored — React Query retains the last
      // successful payload in query.state.data even after a throw, so checking
      // only the cached data is not sufficient to detect a failed tick.
      if (query.state.status === "error") return false;

      const res = query.state.data?.getPayment;
      if (!res || res.code !== "OK") return false;
      const status = res.payment?.paymentStatus;
      if (status === "COMPLETED" || status === "FAILED") return false;
      return 3000;
    }
  });
};
